import { useEffect, useMemo, useState } from "react";
import { Loader2, UserPlus, CheckCircle2, Copy, Check, Mail, AlertTriangle, Eye, FileText, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { fetchFees, createClientFromInterview, previewWelcomeEmail, formatApiError } from "@/lib/api";
import { maskSSN, validateSSN, maskDOB, validateDOB, maskPhone, validatePhone, validateAddress } from "@/lib/format";
import EstimateEditor from "@/components/admin/EstimateEditor";
import WelcomePreview from "@/components/admin/WelcomePreview";
import { COMPLEXITY_ITEMS, COMPLEXITY_CATEGORIES } from "@/data/content";
import StatePicker from "@/components/StatePicker";

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

const FILING_STATUSES = [
  { id: "single", label: "Single" },
  { id: "married-jointly", label: "Married filing jointly" },
  { id: "married-separately", label: "Married filing separately" },
  { id: "head-of-household", label: "Head of household" },
  { id: "qualifying-surviving-spouse", label: "Qualifying surviving spouse" },
];

const CONTACT_METHODS = [
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone call" },
  { id: "text", label: "Text message" },
];

const SELECTABLE = COMPLEXITY_ITEMS.filter((i) => i.id !== "multi-state");

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  address: "",
  filing_status: "single",
  ssn_itin: "",
  dependents: 0,
  occupation: "",
  notes: "",
  date_of_birth: "",
  spouse_name: "",
  spouse_ssn: "",
  spouse_dob: "",
  preferred_contact: "email",
  referral_source: "",
};

export default function NewClientTab({ adminKey, prefill, onPrefillApplied }) {
  const [fees, setFees] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState([]);
  const [states, setStates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [lines, setLines] = useState([]);
  const [fileEstimate, setFileEstimate] = useState(true);
  const [acceptIncomplete, setAcceptIncomplete] = useState(false);
  const [quoteCopied, setQuoteCopied] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...f,
      name: prefill.name || f.name,
      email: prefill.email || f.email,
      phone: prefill.phone || f.phone,
      notes: prefill.notes ? `From enquiry: ${prefill.notes}` : f.notes,
    }));
    if (Array.isArray(prefill.items) && prefill.items.length > 0) {
      setSelected(prefill.items.filter((id) => SELECTABLE.some((i) => i.id === id)));
    }
    if (Array.isArray(prefill.states) && prefill.states.length > 0) {
      setStates(prefill.states);
    }
    toast.success("Enquiry details loaded into the form below.");
    onPrefillApplied?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.ts]);

  useEffect(() => {
    fetchFees().then(setFees).catch(() => setFees(null));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleItem = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const buildDefaultLines = () => {
    if (!fees) return [];
    const feeFor = (id) => (fees.items || []).find((f) => f.id === id);
    const out = [
      {
        key: "base",
        form_id: "base",
        label: "Base return preparation",
        min: Number(fees.base_min) || 0,
        max: Number(fees.base_max) || 0,
      },
    ];
    for (const id of selected) {
      const f = feeFor(id);
      if (!f) continue;
      out.push({
        key: `item:${id}`,
        form_id: id,
        label: COMPLEXITY_ITEMS.find((i) => i.id === id)?.label || id,
        min: Number(f?.min) || 0,
        max: Number(f?.max) || 0,
      });
    }
    const ms = feeFor("multi-state");
    for (const st of states) {
      out.push({
        key: `state:${st}`,
        form_id: "multi-state",
        label: `State return preparation — ${st}`,
        min: Number(ms?.min) || 0,
        max: Number(ms?.max) || 0,
      });
    }
    return out;
  };

  useEffect(() => {
    if (!fees) return;
    setLines((prev) => {
      const defaults = buildDefaultLines();
      const byKey = new Map(prev.map((l) => [l.key, l]));
      const merged = defaults.map((d) => byKey.get(d.key) || d);
      const customs = prev.filter((l) => String(l.key).startsWith("custom:"));
      return [...merged, ...customs];
    });
     
  }, [fees, selected, states]);

  const sum = useMemo(
    () => ({
      min: Math.round(lines.reduce((t, l) => t + (Number(l.min) || 0), 0)),
      max: Math.round(lines.reduce((t, l) => t + (Number(l.max) || 0), 0)),
    }),
    [lines]
  );

  const estimate = sum;

  const REQUIRED = [
    ["name", "Legal name"],
    ["date_of_birth", "Date of birth"],
    ["ssn_itin", "SSN / ITIN (last 4)"],
    ["address", "Mailing address"],
    ["phone", "Phone number"],
    ["filing_status", "Filing status"],
  ];
  const MARRIED = ["married-jointly", "married-separately"];
  const missingFields = REQUIRED.filter(([k]) => !String(form[k] || "").trim()).map(([, l]) => l).concat(
    MARRIED.includes(form.filing_status)
      ? [
          ["spouse_name", "Spouse legal name"],
          ["spouse_dob", "Spouse date of birth"],
          ["spouse_ssn", "Spouse SSN / ITIN (last 4)"],
        ].filter(([k]) => !String(form[k] || "").trim()).map(([, l]) => l)
      : [],
  );

  const formatErrors = [
    validateSSN(form.ssn_itin, { required: false }),
    validateDOB(form.date_of_birth, { required: false }),
    validatePhone(form.phone, { required: false }),
    validateAddress(form.address, { required: false }),
    validateSSN(form.spouse_ssn, { required: false }),
    validateDOB(form.spouse_dob, { required: false, label: "Spouse date of birth" }),
  ].filter(Boolean);

  const copyQuote = async () => {
    const body = [
      `Preparation estimate for ${form.name.trim() || "your return"}: $${Number(sum.min).toLocaleString()} – $${Number(sum.max).toLocaleString()}`,
      "",
      ...lines
        .filter((l) => String(l.label).trim())
        .map((l) => `• ${l.label}: $${Number(l.min || 0).toLocaleString()} – $${Number(l.max || 0).toLocaleString()}`),
      "",
      "Indicative range based on what you've told us — your exact fee is confirmed in writing before any work begins.",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(body);
      setQuoteCopied(true);
      setTimeout(() => setQuoteCopied(false), 2000);
    } catch {
      toast.error("Could not copy the quote.");
    }
  };

  const buildPayload = () => ({
    ...form,
    dependents: Number(form.dependents) || 0,
    selected_items: selected,
    states,
    estimate_lines: lines
      .filter((l) => String(l.label).trim())
      .map((l) => ({
        label: String(l.label).trim(),
        min: Number(l.min) || 0,
        max: Number(l.max) || 0,
        form_id: l.form_id || "",
      })),
    create_estimate_record: fileEstimate,
  });

  const openPreview = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (formatErrors.length > 0) {
      toast.error(formatErrors[0]);
      return;
    }
    if (missingFields.length > 0 && !acceptIncomplete) {
      toast.error("Some required details are blank — choose how to continue.");
      return;
    }
    if (estimate.max < estimate.min) {
      toast.error("The high amount must be at least the low amount.");
      return;
    }
    setPreviewing(true);
    try {
      const data = await previewWelcomeEmail(adminKey, buildPayload());
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const create = async () => {
    setSubmitting(true);
    try {
      const data = await createClientFromInterview(adminKey, buildPayload());
      setPreview(null);
      setResult(data);
      if (data.email_sent) {
        toast.success("Client account created and welcome email sent.");
      } else {
        toast.warning(data.email_error || "Account created, but the welcome email failed to send.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setSelected([]);
    setStates([]);
    setResult(null);
    setCopied(false);
    setLines([]);
    setFileEstimate(true);
  };

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  if (result) {
    return (
      <div className="max-w-2xl" data-testid="new-client-success">
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-8">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-400" />
            <h3 className="font-display text-xl font-medium text-steel-text">Account created</h3>
          </div>
          <p className="mt-2 text-sm text-steel-secondary">
            {result.client.name} ({result.client.email}) now has a client portal account.
          </p>

          <div className="mt-6 flex items-center gap-2 text-sm" data-testid="new-client-email-status">
            <Mail className={`h-4 w-4 ${result.email_sent ? "text-emerald-400" : "text-amber-400"}`} />
            {result.email_sent ? (
              <span className="text-emerald-400">Welcome email sent with sign-in details and their estimate.</span>
            ) : (
              <span className="text-amber-400">{result.email_error || "Welcome email failed to send."}</span>
            )}
          </div>

          <div className="mt-6 border border-steel-border bg-steel-elevated p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Temporary password</p>
            <div className="mt-2 flex items-center justify-between gap-4">
              <span className="font-mono text-lg text-steel-text" data-testid="new-client-temp-password">
                {result.temp_password}
              </span>
              <button
                type="button"
                onClick={copyPw}
                data-testid="copy-temp-password-btn"
                className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="mt-3 flex items-start gap-2 text-xs text-steel-muted">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
              The client is prompted to set their own password on first sign-in. Keep this only as a backup.
            </p>
          </div>

          {estimate && (
            <div className="mt-4 border border-steel-border bg-steel-elevated p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Estimate emailed</p>
              <p className="neon-text mt-1 font-display text-3xl font-medium text-steel-brass" data-testid="new-client-estimate">
                ${result.estimate.min.toLocaleString()} – ${result.estimate.max.toLocaleString()}
              </p>
              {result.estimate_record && (
                <p className="mt-3 flex items-center gap-2 text-xs text-steel-secondary" data-testid="new-client-estimate-record">
                  <FileText className="h-3.5 w-3.5 text-steel-brass" />
                  Filed in Invoices as{" "}
                  <span className="font-mono text-steel-text">{result.estimate_record.number}</span> — convert it
                  to an invoice when you&rsquo;re ready to bill.
                </p>
              )}
              {result.documents?.length > 0 && (
                <p className="mt-2 flex items-center gap-2 text-xs text-steel-secondary" data-testid="new-client-documents-count">
                  <ClipboardList className="h-3.5 w-3.5 text-amber-400" />
                  {result.documents.length} documents requested — listed in the email and waiting on their portal
                  checklist.
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={reset}
            data-testid="new-client-another-btn"
            className="mt-8 flex items-center gap-2 border border-steel-brass bg-steel-brass/10 px-6 py-3 text-sm font-medium text-steel-text transition-colors hover:bg-steel-brass/20"
          >
            <UserPlus className="h-4 w-4" /> Add another client
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={openPreview} className="grid grid-cols-1 gap-10 lg:grid-cols-12" data-testid="new-client-form">
      <div className="lg:col-span-7 space-y-8">
        <div>
          <h3 className="font-display text-xl font-medium text-steel-text">Client interview</h3>
          <p className="mt-1 text-sm text-steel-secondary">
            Capture the client's details and situation. We'll create their portal account and email sign-in
            instructions plus a preliminary estimate.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Full name *</label>
            <input data-testid="nc-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Email *</label>
            <input data-testid="nc-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@example.com" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Phone</label>
            <input data-testid="nc-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", maskPhone(e.target.value))} placeholder="(603) 555-0100" inputMode="tel" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Occupation</label>
            <input data-testid="nc-occupation" className={inputCls} value={form.occupation} onChange={(e) => set("occupation", e.target.value)} placeholder="Engineer" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Address</label>
            <input data-testid="nc-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="12 Main St, Concord NH 03301" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Filing status</label>
            <select data-testid="nc-filing-status" className={inputCls} value={form.filing_status} onChange={(e) => set("filing_status", e.target.value)}>
              {FILING_STATUSES.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Dependents</label>
            <input data-testid="nc-dependents" type="number" min="0" max="30" className={inputCls} value={form.dependents} onChange={(e) => set("dependents", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">SSN / ITIN — last 4</label>
            <input data-testid="nc-ssn" className={inputCls} value={form.ssn_itin} onChange={(e) => set("ssn_itin", maskSSN(e.target.value))} placeholder="Last 4 (e.g. 6789)" inputMode="numeric" maxLength={4} />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Date of birth</label>
            <input data-testid="nc-dob" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", maskDOB(e.target.value))} placeholder="MM/DD/YYYY" inputMode="numeric" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Preferred contact</label>
            <select data-testid="nc-preferred-contact" className={inputCls} value={form.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)}>
              {CONTACT_METHODS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Spouse name</label>
            <input data-testid="nc-spouse-name" className={inputCls} value={form.spouse_name} onChange={(e) => set("spouse_name", e.target.value)} placeholder="If filing jointly" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Spouse date of birth</label>
            <input data-testid="nc-spouse-dob" className={inputCls} value={form.spouse_dob} onChange={(e) => set("spouse_dob", maskDOB(e.target.value))} placeholder="MM/DD/YYYY" inputMode="numeric" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Spouse SSN / ITIN — last 4</label>
            <input data-testid="nc-spouse-ssn" className={inputCls} value={form.spouse_ssn} onChange={(e) => set("spouse_ssn", maskSSN(e.target.value))} placeholder="Last 4 (e.g. 6789)" inputMode="numeric" maxLength={4} />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Referral source</label>
            <input data-testid="nc-referral" className={inputCls} value={form.referral_source} onChange={(e) => set("referral_source", e.target.value)} placeholder="How did they hear about us?" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Notes</label>
            <textarea data-testid="nc-notes" rows={3} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything relevant from the interview…" />
          </div>
        </div>

        <div>
          <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Tax situation (drives the estimate)</label>
          <div className="space-y-6">
            {COMPLEXITY_CATEGORIES.map((cat) => {
              const items = SELECTABLE.filter((i) => i.cat === cat.id);
              if (items.length === 0) return null;
              return (
                <div key={cat.id}>
                  <p className="mb-2 text-sm font-medium text-steel-text">{cat.label}</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {items.map((item) => {
                      const on = selected.includes(item.id);
                      const fee = (fees?.items || []).find((f) => f.id === item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-testid={`nc-item-${item.id}`}
                          onClick={() => toggleItem(item.id)}
                          className={`flex items-center justify-between gap-3 border px-4 py-2.5 text-left text-sm transition-colors ${
                            on
                              ? "border-steel-brass bg-steel-brass/10 text-steel-text"
                              : "border-steel-border text-steel-secondary hover:border-steel-muted"
                          }`}
                        >
                          <span>{item.label}</span>
                          {fee && (
                            <span className="shrink-0 font-mono text-[10px] text-steel-muted">
                              +${Number(fee.min).toLocaleString()}–${Number(fee.max).toLocaleString()}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">States filed (outside NH)</label>
          <StatePicker selected={states} onChange={setStates} />
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto border border-steel-border bg-steel-surface p-7">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-muted">Preliminary estimate</p>
          <div className="mt-3 border border-steel-brass/40 bg-steel-elevated p-5" data-testid="nc-quote-box">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-brass">Quote for this client</p>
            <p className="neon-text mt-1.5 font-display text-3xl font-medium text-steel-brass" data-testid="nc-quote-total">
              ${Number(sum.min).toLocaleString()} – ${Number(sum.max).toLocaleString()}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
              {selected.length} situation{selected.length === 1 ? "" : "s"}
              {states.length > 0 ? ` · ${states.length} state${states.length === 1 ? "" : "s"}` : ""} · {lines.length} line
              {lines.length === 1 ? "" : "s"}
            </p>
            <button
              type="button"
              data-testid="nc-copy-quote"
              onClick={copyQuote}
              className="mt-4 flex w-full items-center justify-center gap-2 border border-steel-border px-4 py-2.5 text-xs font-medium text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
            >
              {quoteCopied ? <Check className="h-3.5 w-3.5 text-steel-brass" /> : <Copy className="h-3.5 w-3.5" />}
              {quoteCopied ? "Quote copied" : "Copy quote for the call"}
            </button>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-steel-muted">
            Edit any line or add your own — the total calculates automatically from individual pricing, and this
            is exactly what the client sees in the welcome email.
          </p>
          {fees ? (
            <div className="mt-6">
              <EstimateEditor
                lines={lines}
                setLines={setLines}
                sum={sum}
                onResetLines={() => setLines(buildDefaultLines())}
              />
            </div>
          ) : (
            <p className="mt-3 text-sm text-steel-muted">Loading fees…</p>
          )}

          <label className="mt-6 flex cursor-pointer items-start gap-3 border border-steel-border bg-steel-elevated p-4">
            <input
              type="checkbox"
              data-testid="nc-file-estimate"
              checked={fileEstimate}
              onChange={(e) => setFileEstimate(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-purple-600"
            />
            <span className="text-xs leading-relaxed text-steel-secondary">
              <span className="flex items-center gap-1.5 font-medium text-steel-text">
                <FileText className="h-3.5 w-3.5 text-steel-brass" /> File this as an Estimate
              </span>
              Saves it in the Invoices area as an estimate record the client can view (not payable). Convert it
              to a real invoice when you&rsquo;re ready to bill.
            </span>
          </label>

          {formatErrors.length > 0 && (
            <div className="mt-6 border-2 border-red-500/60 bg-red-950/25 p-5" data-testid="nc-format-panel">
              <p className="flex items-center gap-2 text-sm font-medium text-red-300">
                <AlertTriangle className="h-4 w-4" /> Fix these before continuing
              </p>
              <ul className="mt-2 space-y-1 pl-5 text-xs leading-relaxed text-red-200/90">
                {formatErrors.map((err) => (
                  <li key={err} className="list-disc">{err}</li>
                ))}
              </ul>
            </div>
          )}

          {missingFields.length > 0 && (
            <div className="mt-6 border-2 border-amber-500/60 bg-amber-950/20 p-5" data-testid="nc-missing-panel">
              <p className="flex items-center gap-2 text-sm font-medium text-amber-300">
                <AlertTriangle className="h-4 w-4" /> {missingFields.length} required detail
                {missingFields.length === 1 ? "" : "s"} still blank
              </p>
              <p className="mt-2 text-xs leading-relaxed text-amber-200/80">{missingFields.join(", ")}</p>
              <p className="mt-3 text-xs leading-relaxed text-steel-secondary">
                Fill them in above, or create the account anyway — the client will be required to enter and verify
                every blank item when they set their password on first sign-in, and they can&rsquo;t reach the portal
                until it&rsquo;s complete.
              </p>
              <label className="mt-4 flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  data-testid="nc-accept-incomplete"
                  checked={acceptIncomplete}
                  onChange={(e) => setAcceptIncomplete(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-amber-500"
                />
                <span className="text-xs leading-relaxed text-amber-200">
                  Create it anyway — the client completes these details themselves
                </span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={previewing || !fees || formatErrors.length > 0 || (missingFields.length > 0 && !acceptIncomplete)}
            data-testid="nc-submit"
            className="mt-6 flex w-full items-center justify-center gap-2 border border-steel-brass bg-steel-brass/10 px-6 py-3.5 text-sm font-medium text-steel-text transition-colors hover:bg-steel-brass/20 disabled:opacity-50"
          >
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            {previewing
              ? "Building preview…"
              : missingFields.length > 0 && !acceptIncomplete
                ? `Complete ${missingFields.length} required detail${missingFields.length === 1 ? "" : "s"} to continue`
                : "Preview welcome email"}
          </button>
        </div>
      </div>

      {preview && (
        <WelcomePreview
          preview={preview}
          sending={submitting}
          onSend={create}
          onClose={() => setPreview(null)}
        />
      )}
    </form>
  );
}
